import { CreditPackage } from "@/global/store/data/types";
import { Divider } from "@/shared/base/components/Divider/Divider";
import {
  Box,
  Grid,
  Paper,
  Stack,
  useTheme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { CreditPackageItem } from "./CreditPackageItem";
import React from "react";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { CreditPackageSelectedState } from "@/global/store/data/purchaseCredit";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";

interface Props {
  title?: string;
  description: string;
  creditPackages: CreditPackage[];
  selectedCreditPackage: CreditPackageSelectedState;
  onSelectCreditPackage: (pkg: CreditPackageSelectedState) => void;
  isLoading?: boolean;
}

export function PurchaseCreditSection(props: Props) {
  const { sm: smDown } = useCssMediaQuery("down");
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down(1020));

  return (
    <Paper variant="outlined" sx={{ borderRadius: smDown ? 2 : 1 }}>
      <Stack direction={"column"} spacing={1} padding={smDown ? 3 : 4}>
        {props.title ? (
          <Typography variant={"h5"} fontWeight={700}>
            {props.title}
          </Typography>
        ) : null}

        {props.description ? (
          <Typography fontSize={"1rem"} color="text.secondary">
            {props.description}
          </Typography>
        ) : null}
      </Stack>
      <Divider />

      {props.isLoading ? (
        <Box p={smDown ? 3 : 4}>
          <CreditPackagesSkeleton />
        </Box>
      ) : null}

      {!props.isLoading && props.creditPackages.length === 0 ? (
        <Stack
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"340px"}
          p={smDown ? 2 : 4}
        >
          <EmptyState
            title="No credit packages available"
            description="There are no credit packages available for purchase at this time."
          />
        </Stack>
      ) : null}

      {!props.isLoading && props.creditPackages.length > 0 ? (
        <Grid container spacing={smDown ? 3 : 4} padding={smDown ? 3 : 3}>
          {props.creditPackages.map((itm) => (
            <Grid
              key={itm.id}
              item
              xs={12}
              md={
                props.creditPackages.length == 1
                  ? 12
                  : props.creditPackages.length == 2
                  ? 6
                  : isMobile
                  ? 12
                  : props.creditPackages.length == 4
                  ? 6
                  : 4
              }
            >
              <CreditPackageItem
                title={itm.title}
                description={itm.description}
                amount={itm.amount}
                onSelect={() =>
                  props.onSelectCreditPackage({
                    id: itm.id,
                    amount: itm.amount,
                    serviceFee: Number(itm.serviceFee),
                    isCustom: false,

                    eventCode: itm?.eventCode,
                    className: itm?.className,
                    ticketTypeCode: itm?.ticketTypeCode,
                    priceCode: itm?.priceCode,
                    seatCount: itm?.seatCount,
                    sectionName: itm?.sectionName,
                    accountGroupId: itm?.accountGroupId,
                    accountGroupName: itm?.accountGroupName,
                    title: itm?.title,
                  })
                }
                isSelected={props?.selectedCreditPackage?.id === itm.id}
                isLoading={props.isLoading}
                isDisabled={
                  !!props?.selectedCreditPackage?.amount &&
                  props.selectedCreditPackage?.amount !== itm.amount
                }
                backgroundColor={itm?.backgroundColor}
              />
            </Grid>
          ))}
        </Grid>
      ) : null}
    </Paper>
  );
}

export function CreditPackagesSkeleton() {
  const { sm: smDown, md: mdDown } = useCssMediaQuery("down");

  return (
    <Box>
      <Grid container spacing={smDown ? 3 : 4}>
        {Array.from({ length: 2 })
          .fill(1)
          .map((itm, idx) => {
            return (
              <Grid key={idx} item xs={12} md={6}>
                <DynamicSkeleton
                  rowCount={4}
                  spacing={2}
                  widths={["100%", "100%", "80%", "60%"]}
                  heights={["120px", "20px", "20px", "20px"]}
                />
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
}
